import { trackVercelEvent } from './vercel-analytics'

export type DashboardV3OptInSource = 'v2-banner' | 'v2-profile'

type DashboardV3OptInContext = {
  currentHost: string
  organizationId?: string | null
  source: DashboardV3OptInSource
  user?: string | null
  userSub?: string | null
}

const bannerDismissedStorageKeyPrefix = 'dashboard-v3-opt-in-dismissed'

const dashboardV3DestinationByHost: Record<string, string> = {
  'dbstaging.kenai.co.za': 'https://dbstaging.kenai.app',
  'dashboard.kenai.co.za': 'https://dashboard.kenai.app',
  'eskom.kenai.co.za': 'https://eskom.kenai.app',
  'fic.kenai.co.za': 'https://fic.kenai.app',
  'gijima.kenai.co.za': 'https://gijima.kenai.app',
  'nedbankdb.kenai.co.za': 'https://nedbank.kenai.app',
  'ninetyone.kenai.app': 'https://ninetyone-v3.kenai.app',
  'ninetyone.kenai.co.za': 'https://ninetyone-v3.kenai.app',
  'opp-gen.kenai.co.za': 'https://opp-gen.kenai.app',
  'payinc.kenai.co.za': 'https://payinc.kenai.app',
  'syngenta.kenai.co.za': 'https://syngenta.kenai.app',
  localhost: 'http://localhost:3000',
}

const clickEventBySource: Record<DashboardV3OptInSource, string> = {
  'v2-banner': 'dashboard_v3_banner_clicked',
  'v2-profile': 'dashboard_v3_profile_entry_clicked',
}

function normalizeHost(host: string) {
  return host.trim().toLowerCase()
}

function getLookupHost(host: string) {
  return normalizeHost(host).split(':')[0]
}

function getSessionStorage(storage?: Storage | null) {
  if (storage) {
    return storage
  }

  if (typeof window === 'undefined') {
    return null
  }

  return window.sessionStorage
}

export function getDashboardV3Destination(host: string) {
  return dashboardV3DestinationByHost[getLookupHost(host)] ?? null
}

export function createDashboardV3OptInUrl(host: string, source: DashboardV3OptInSource) {
  const destination = getDashboardV3Destination(host)

  if (!destination) {
    return null
  }

  const url = new URL('/', destination)
  url.searchParams.set('from_host', getLookupHost(host))
  url.searchParams.set('source', source)

  return url.toString()
}

export function getDashboardV3BannerDismissalStorageKey(host: string) {
  return `${bannerDismissedStorageKeyPrefix}:${getLookupHost(host)}`
}

export function dismissDashboardV3Banner(host: string, storage?: Storage | null) {
  const sessionStorage = getSessionStorage(storage)

  if (!sessionStorage) {
    return
  }

  sessionStorage.setItem(getDashboardV3BannerDismissalStorageKey(host), '1')
}

export function isDashboardV3BannerDismissed(host: string, storage?: Storage | null) {
  const sessionStorage = getSessionStorage(storage)

  if (!sessionStorage) {
    return false
  }

  return sessionStorage.getItem(getDashboardV3BannerDismissalStorageKey(host)) === '1'
}

export function createDashboardV3OptInAnalyticsPayload(context: DashboardV3OptInContext) {
  const destination = getDashboardV3Destination(context.currentHost)

  if (!destination) {
    return null
  }

  return {
    from_host: getLookupHost(context.currentHost),
    organization_id: context.organizationId || null,
    source: context.source,
    to_host: new URL(destination).host,
    user: context.user ? context.user.toLowerCase() : null,
    user_sub: context.userSub || null,
  }
}

export function trackDashboardV3OptInEvent(eventName: string, context: DashboardV3OptInContext) {
  const payload = createDashboardV3OptInAnalyticsPayload(context)

  if (!payload) {
    return false
  }

  trackVercelEvent(eventName, payload)
  return true
}

export function startDashboardV3OptIn(context: DashboardV3OptInContext) {
  const url = createDashboardV3OptInUrl(context.currentHost, context.source)

  if (!url) {
    return false
  }

  trackDashboardV3OptInEvent(clickEventBySource[context.source], context)
  window.location.assign(url)

  return true
}
